<template>
  <div :class="`propeller-address-selector ${className || ''}`">
    <button
      type="button"
      class="propeller-address-selector__trigger inline-flex items-center gap-2 px-4 py-2 border border-input rounded-[var(--radius-control)] text-sm font-medium text-muted-foreground bg-card hover:bg-surface-hover transition-colors"
      @click="
        async (event) => {
          showModal = true;
        }
      "
    >
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-4 h-4">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
          :strokeWidth="2"
        ></path>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
          :strokeWidth="2"
        ></path></svg
      >{{ getLabel('chooseAddress', 'Choose address') }}
    </button>
    <template v-if="showModal">
      <div
        class="propeller-address-selector__modal fixed inset-0 bg-black/50 flex items-start justify-center z-50 overflow-y-auto py-10"
      >
        <div class="propeller-address-selector__modal-content bg-card p-6 rounded-[var(--radius-container)] max-w-2xl w-full mx-4 shadow-xl">
          <div class="propeller-address-selector__modal-header flex justify-between items-center mb-6">
            <h3 class="propeller-address-selector__modal-title text-xl font-bold">
              {{ getLabel('modalTitle', 'Choose an address') }}
            </h3>
            <button
              type="button"
              class="propeller-address-selector__modal-close text-muted-foreground hover:text-muted-foreground text-xl leading-none"
              @click="
                async (event) => {
                  showModal = false;
                }
              "
            >
              &times;
            </button>
          </div>
          <template v-if="getAddresses().length === 0">
            <p class="propeller-address-selector__empty text-muted-foreground italic">
              {{ getLabel('noAddresses', 'No addresses found.') }}
            </p>
          </template>

          <template v-if="getAddresses().length > 0">
            <div class="propeller-address-selector__list grid grid-cols-2 gap-4">
              <template :key="address.id" v-for="(address, index) in getAddresses()">
                <div
                  @click="async (event) => handleTileClick(address)"
                  :data-selected="selectedAddress?.id === address.id ? 'true' : 'false'"
                  :class="`propeller-address-selector__option cursor-pointer rounded-[var(--radius-container)] transition-all ring-2 ${
                    selectedAddress?.id === address.id
                      ? 'ring-primary'
                      : 'ring-transparent hover:ring-primary/40'
                  }`"
                >
                  <component
                    :is="AddressCardImpl"
                    :address="address"
                    :enableActions="false"
                    :countries="countries"
                    :showFullName="true"
                    :showStreet="true"
                    :showPostalCode="true"
                    :showCity="true"
                    :showCountry="true"
                    :showNumberExtension="true"
                  ></component>
                </div>
              </template>
            </div>
            <div class="propeller-address-selector__modal-actions flex justify-end mt-6 pt-4 border-t border-border-subtle">
              <button
                type="button"
                class="propeller-address-selector__confirm-btn inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-[var(--radius-control)] text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                :disabled="!selectedAddress || isLoading"
                @click="async (event) => handleConfirm()"
              >
                <template v-if="isLoading">
                  <svg fill="none" viewBox="0 0 24 24" class="w-4 h-4 animate-spin">
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      class="opacity-25"
                    ></circle>
                    <path
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      class="opacity-75"
                    ></path>
                  </svg>
                </template>

                <template v-if="isLoading">
                  {{ getLabel('updating', 'Updating...') }}
                </template>

                <template v-else>
                  {{ getLabel('useThisAddress', 'Use this address') }}
                </template>
              </button>
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, type Component } from 'vue';

import { Address, AddressType, Company, Contact, Customer } from '@propeller-commerce/propeller-sdk-v2';
import DefaultAddressCard from './AddressCard.vue';
import { getLabel as _getLabel } from '@propeller-commerce/propeller-v2-core-ui';

export interface AddressSelectorProps {
  /** Authenticated user — addresses are derived from their profile. Resolved from PropellerProvider when omitted. */
  user?: Contact | Customer | null;

  /**
   * Active company ID (for Contact users).
   * Pass the value from the company switcher so the correct company's addresses are listed.
   */
  companyId?: number;

  /**
   * Filter addresses to this type.
   * Defaults to AddressType.delivery.
   */
  addressType?: string;

  /** Called when the user picks an address from the modal. Supports async. */
  onAddressSelected?: (address: Address) => void | Promise<void>;

  /** Country list forwarded to AddressCard [{code: 'NL', name: 'Netherlands'}, ...] */
  countries?: {
    code: string;
    name: string;
  }[];

  /** Label overrides. Keys: chooseAddress, modalTitle, noAddresses */
  labels?: Record<string, string>;

  /** Extra CSS class on the root element. */
  className?: string;

  // ───── Extension API ─────
  // Replaces each <AddressCard> rendered inside the selector list.
  addressCardComponent?: Component;
}
interface AddressSelectorState {
  showModal: boolean;
  selectedAddress: Address | null;
  isLoading: boolean;
  getLabel: (key: string, fallback: string) => string;
  getActiveCompany: () => Company | null;
  getAddresses: () => Address[];
  handleTileClick: (address: Address) => void;
  handleConfirm: () => Promise<void>;
}

const props = defineProps<AddressSelectorProps>();
const showModal = ref<AddressSelectorState['showModal']>(false);
const selectedAddress = ref<AddressSelectorState['selectedAddress']>(null);
const isLoading = ref<AddressSelectorState['isLoading']>(false);
const AddressCardImpl = computed(() => props.addressCardComponent ?? DefaultAddressCard);

function getLabel(key: string, fallback: string): ReturnType<AddressSelectorState['getLabel']> {
  return _getLabel(props.labels, key, fallback);
}
function getActiveCompany(): ReturnType<AddressSelectorState['getActiveCompany']> {
  const user = props.user as Contact | Customer | null;
  if (!user || !('contactId' in user)) return null;
  const contact = user as Contact;
  const cid = props.companyId as number;
  if (cid) {
    const companiesRaw = (contact as any).companies;
    const items = (companiesRaw?.items ?? companiesRaw) as Company[] | undefined;
    if (Array.isArray(items)) {
      for (let i = 0; i < items.length; i++) {
        if (items[i].companyId === cid) return items[i];
      }
    }
    if ((contact.company as Company)?.companyId === cid) return contact.company as Company;
  }
  return (contact.company as Company | undefined) ?? null;
}
function getAddresses(): ReturnType<AddressSelectorState['getAddresses']> {
  const user = props.user as Contact | Customer | null;
  if (!user) return [];
  const type = (props.addressType as string) || AddressType.delivery;
  let all: Address[] = [];
  if ('contactId' in user) {
    const company = getActiveCompany();
    all = ((company as any)?.addresses || []) as Address[];
  } else if ('customerId' in user) {
    all = ((user as Customer).addresses || []) as Address[];
  }
  return all.filter((a: Address) => a.type === type);
}
function handleTileClick(address: Address): ReturnType<AddressSelectorState['handleTileClick']> {
  selectedAddress.value = address;
}
async function handleConfirm(): ReturnType<AddressSelectorState['handleConfirm']> {
  if (!selectedAddress.value || isLoading.value) return;
  isLoading.value = true;
  try {
    if (props.onAddressSelected) {
      await props.onAddressSelected(selectedAddress.value as Address);
    }
    showModal.value = false;
    selectedAddress.value = null as any;
  } finally {
    isLoading.value = false;
  }
}
</script>
